import { useEffect, useRef, useState } from "react";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import LogOut from "lucide-react/dist/esm/icons/log-out";
import Volume2 from "lucide-react/dist/esm/icons/volume-2";
import VolumeX from "lucide-react/dist/esm/icons/volume-x";
import Wifi from "lucide-react/dist/esm/icons/wifi";
import WifiOff from "lucide-react/dist/esm/icons/wifi-off";
import { canPlay, type Card, type EmoteKind, type PlayerSummary, type RoomView } from "../../game";
import { haptic, sound } from "../feedback";
import { flyCardFace, flyPotToStock } from "../fx";
import type { ConnectionStatus, EmoteEvent } from "../use-game";
import { CardBack, PlayingCard } from "./playing-card";

const EMOTE_EMOJI: Record<EmoteKind, string> = {
  fire: "🔥",
  laugh: "😂",
  turtle: "🐢",
  shock: "😱",
};

const LOW_CARDS_THRESHOLD = 6;
const HIDDEN_HAND_BACKS = 3;

interface GameBoardProps {
  room: RoomView;
  connection: ConnectionStatus;
  latency: number | null;
  emotes: EmoteEvent[];
  soundEnabled: boolean;
  hapticsEnabled: boolean;
  chinPending: boolean;
  onToggleSound: () => void;
  onOpenRules: () => void;
  onPlay: (cardId: string, pileIndex: 0 | 1) => void;
  onChin: () => void;
  onRefresh: () => void;
  onReady: () => void;
  onEmote: (emote: EmoteKind) => void;
  onLeave: () => void;
}

function cardsLeft(player: PlayerSummary): number {
  return player.cardCount;
}

function Stock({ count, owner }: { count: number; owner: string }) {
  return (
    <div className="stock" aria-label={`${owner}: ${count} cartas en el mazo`}>
      {count > 0 ? <CardBack label={`Mazo de ${owner}`} compact /> : <span className="empty-stock" />}
      <strong>{count}</strong>
    </div>
  );
}

function CardMeter({ left, total }: { left: number; total: number }) {
  const low = left <= LOW_CARDS_THRESHOLD;
  return (
    <div className={`card-meter ${low ? "is-low" : ""}`} aria-label={`${left} cartas restantes de ${total}`}>
      <i style={{ width: `${Math.min(100, Math.max(3, Math.round((left / total) * 100)))}%` }} />
    </div>
  );
}

function PlayerPanel({ player, total }: { player: PlayerSummary; total: number }) {
  return (
    <div className="player-score" aria-label={`${player.name}: ${player.chinCount} CHIN acertados, ${cardsLeft(player)} cartas restantes`}>
      <strong>{player.name}</strong>
      <span>{player.chinCount} CHIN</span>
      <CardMeter left={cardsLeft(player)} total={total} />
    </div>
  );
}

function Rack({
  player,
  room,
  own,
  locked,
  onPlay,
  onInvalid,
}: {
  player: PlayerSummary;
  room: RoomView;
  own: boolean;
  locked: boolean;
  onPlay: (cardId: string, pileIndex: 0 | 1) => void;
  onInvalid: () => void;
}) {
  return (
    <div
      className={`rack ${own ? "rack--own" : "rack--rival"}`}
      role="group"
      aria-label={`Cartas visibles de ${player.name}`}
    >
      {Array.from({ length: 4 }, (_, index) => {
        const card = player.rack[index] ?? null;
        if (!card) return <span className="rack-slot" key={`slot-${index}`} aria-label="Espacio vacío" />;
        const stackCount = Math.max(1, player.rackStackCounts[index] ?? 1);
        const playablePiles = ([0, 1] as const).filter((pileIndex) => canPlay(card, room.piles[pileIndex].top));
        return (
          <div className={`rack-card-stack ${stackCount > 1 ? "is-stacked" : ""}`} key={card.id}>
            {stackCount > 1 ? <span className="rack-card-stack__layers" aria-hidden="true" /> : null}
            <PlayingCard
              card={card}
              size="rack"
              interactive={own && room.phase === "playing" && !locked}
              stackCount={stackCount}
              playablePiles={playablePiles}
              onPlay={(pileIndex) => onPlay(card.id, pileIndex)}
              onInvalid={onInvalid}
            />
          </div>
        );
      })}
    </div>
  );
}

function OwnHand({
  player,
  room,
  locked,
  onPlay,
  onInvalid,
}: {
  player: PlayerSummary;
  room: RoomView;
  locked: boolean;
  onPlay: (cardId: string, pileIndex: 0 | 1) => void;
  onInvalid: () => void;
}) {
  const cards = player.hand ?? [];
  return (
    <div className="hand-zone">
      <div className="hand-mode-heading">
        <strong>CARTA EN MANO</strong>
        <span>{player.cardCount} {player.cardCount === 1 ? "carta privada" : "cartas privadas"}</span>
      </div>
      <div className="hand-cards" role="group" aria-label={`Tus ${player.cardCount} cartas privadas`}>
        {cards.map((card) => {
          const playablePiles = ([0, 1] as const).filter((pileIndex) => canPlay(card, room.piles[pileIndex].top));
          return (
            <div className="hand-card" key={card.id}>
              <PlayingCard
                card={card}
                size="rack"
                interactive={room.phase === "playing" && !locked}
                playablePiles={playablePiles}
                onPlay={(pileIndex) => onPlay(card.id, pileIndex)}
                onInvalid={onInvalid}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HiddenHand({ player }: { player: PlayerSummary }) {
  const visibleBacks = Math.min(HIDDEN_HAND_BACKS, player.cardCount);
  return (
    <div
      className="hidden-hand"
      role="img"
      aria-label={`${player.name} está en Carta en mano y tiene ${player.cardCount} cartas ocultas`}
    >
      <div className="hidden-hand__cards" aria-hidden="true">
        {Array.from({ length: visibleBacks }, (_, index) => (
          <CardBack key={index} compact />
        ))}
        <strong className="hidden-hand__count">{player.cardCount}</strong>
      </div>
      <span className="hidden-hand__label" aria-hidden="true">
        <strong>CARTA EN MANO</strong>
        <small>cartas ocultas</small>
      </span>
    </div>
  );
}

function CenterPile({ card, count, index, hot }: { card: Card | null; count: number; index: 0 | 1; hot: boolean }) {
  return (
    <div className={`center-pile ${hot ? "is-hot" : ""}`} data-pile-index={index}>
      <span className="pile-shadow pile-shadow--two" aria-hidden="true" />
      <span className="pile-shadow pile-shadow--one" aria-hidden="true" />
      {card ? <PlayingCard card={card} size="center" /> : <span className="empty-pile">PILA {index + 1}</span>}
      <small>{count} {count === 1 ? "carta" : "cartas"}</small>
    </div>
  );
}

export function GameBoard({
  room,
  connection,
  latency,
  emotes,
  soundEnabled,
  hapticsEnabled,
  chinPending,
  onToggleSound,
  onOpenRules,
  onPlay,
  onChin,
  onRefresh,
  onReady,
  onEmote,
  onLeave,
}: GameBoardProps) {
  const hot = room.chinAvailable;
  const paused = room.phase === "paused" || connection !== "online";
  const finished = room.phase === "finished";

  const self = room.players.find((player) => player.id === room.selfId);
  const rival = room.players.find((player) => player.id !== room.selfId);
  const selfHandMode = self?.handMode ?? false;
  const rivalHandMode = rival?.handMode ?? false;

  const cooldownMs = self?.chinCooldownMs ?? 0;
  const [chinLocked, setChinLocked] = useState(false);
  const [localChinPending, setLocalChinPending] = useState(false);
  const [chinBurst, setChinBurst] = useState(false);
  const [centerPulsing, setCenterPulsing] = useState(false);
  const [expiredProtectionKey, setExpiredProtectionKey] = useState<string | null>(null);
  const handledEventAt = useRef(0);
  const previousHot = useRef(hot);
  const chinSubmitLocked = useRef(false);
  const observedPending = useRef(false);
  const protectionWinnerId = room.chinProtection?.winnerId ?? null;
  const protectionRemainingMs = room.chinProtection?.remainingMs ?? 0;
  const protectionKey = protectionWinnerId ? `${protectionWinnerId}:${room.revision}` : null;
  const protectionActive =
    protectionKey !== null && protectionKey !== expiredProtectionKey && protectionRemainingMs > 0;
  const protectionBlocking = protectionActive && !hot;
  const chinBusy = chinPending || localChinPending;

  useEffect(function clearChinBurst() {
    if (!chinBurst) return;
    const timer = window.setTimeout(() => setChinBurst(false), 380);
    return () => window.clearTimeout(timer);
  }, [chinBurst]);

  useEffect(function trackChinCooldown() {
    if (cooldownMs <= 0) {
      setChinLocked(false);
      return;
    }
    setChinLocked(true);
    const timer = window.setTimeout(() => setChinLocked(false), cooldownMs);
    return () => window.clearTimeout(timer);
  }, [cooldownMs, room.revision]);

  useEffect(function synchronizeChinPending() {
    if (chinPending) {
      observedPending.current = true;
      setLocalChinPending(true);
      return;
    }
    if (observedPending.current) {
      observedPending.current = false;
      setLocalChinPending(false);
    }
  }, [chinPending]);

  useEffect(function releaseUnconfirmedChinLock() {
    if (!localChinPending || chinPending) return;
    const timer = window.setTimeout(() => setLocalChinPending(false), 2_500);
    return () => window.clearTimeout(timer);
  }, [localChinPending, chinPending]);

  useEffect(function releaseChinSubmitGuard() {
    if (!chinPending && !localChinPending) chinSubmitLocked.current = false;
  }, [chinPending, localChinPending]);

  useEffect(function trackChinProtection() {
    if (!protectionKey) {
      setExpiredProtectionKey(null);
      return;
    }
    const timer = window.setTimeout(
      () => setExpiredProtectionKey(protectionKey),
      Math.max(0, protectionRemainingMs),
    );
    return () => window.clearTimeout(timer);
  }, [protectionKey, protectionRemainingMs]);

  useEffect(function announceChinWindow() {
    if (hot && !previousHot.current && !finished) {
      sound("alert", soundEnabled);
      haptic("chin", hapticsEnabled);
    }
    previousHot.current = hot;
  }, [hot, finished, soundEnabled, hapticsEnabled]);

  useEffect(function animateMatchEvents() {
    const event = room.lastEvent;
    if (!event || event.at === handledEventAt.current) return;
    handledEventAt.current = event.at;

    if (event.type === "card-played" && event.actorId !== room.selfId) {
      flyCardFace(
        event.card,
        rivalHandMode ? ".hidden-hand" : ".rack--rival",
        `[data-pile-index="${event.pileIndex}"]`,
      );
      sound("card", soundEnabled);
      return;
    }
    if (event.type === "chin-claimed") {
      const penalizedIsSelf = event.penalizedPlayerId === room.selfId;
      const stockTarget = penalizedIsSelf
        ? selfHandMode ? ".player-zone--self .hand-zone" : ".player-zone--self .stock"
        : rivalHandMode ? ".player-zone--rival .hidden-hand" : ".player-zone--rival .stock";
      flyPotToStock('[data-pile-index="0"]', stockTarget, event.capturedCount, true);
      flyPotToStock('[data-pile-index="1"]', stockTarget, event.capturedCount, false);
      if (penalizedIsSelf) {
        sound("error", soundEnabled);
        haptic("error", hapticsEnabled);
      }
      return;
    }
    if (event.type === "center-refreshed") {
      setCenterPulsing(true);
      const timer = window.setTimeout(() => setCenterPulsing(false), 700);
      return () => window.clearTimeout(timer);
    }
    if (event.type === "match-finished") {
      sound(event.winnerId === room.selfId ? "chin" : "error", soundEnabled);
    }
  }, [room.lastEvent, room.selfId, soundEnabled, hapticsEnabled, selfHandMode, rivalHandMode]);

  if (!self || !rival) return null;

  const totalPerPlayer = room.settings.decks * 26;
  const winner = room.players.find((player) => player.id === room.winnerId);
  const won = room.winnerId === room.selfId;
  const seriesActive = self.wins + rival.wins > 0;
  const interactionLocked = paused || finished || room.stalled;
  const stalledPlayer = room.players.find((player) => player.id === room.stalledPlayerId);
  const selfMustRefresh = room.stalled && room.stalledPlayerId === self.id;
  const protectionWinner = room.players.find((player) => player.id === protectionWinnerId);
  const chinDisabled =
    paused || finished || room.stalled || chinLocked || chinBusy || protectionBlocking;
  const chinKicker = chinBusy
    ? "CHIN ENVIADO"
    : protectionBlocking
      ? protectionWinnerId === self.id ? "TU CHIN" : `${protectionWinner?.name ?? "RIVAL"} CANTÓ`
      : chinLocked
        ? "RESPIRÁ"
        : room.stalled
          ? "MESA BLOQUEADA"
          : hot
            ? "¡AHORA!"
            : "CANTÁ";
  const chinMain = chinBusy
    ? "REVISANDO"
    : protectionBlocking
      ? "PROTEGIDO"
      : chinLocked || room.stalled
        ? "PAUSA"
        : "CHIN!";
  const chinAnnouncement = chinBusy
    ? "CHIN enviado. Estamos verificando el resultado."
    : protectionBlocking
      ? `${protectionWinnerId === self.id ? "Tu CHIN" : `El CHIN de ${protectionWinner?.name ?? "tu rival"}`} está protegido. Otro toque no genera penalización.`
      : hot
        ? "Hay CHIN. Cantá ahora."
        : "";

  function invalidCard(): void {
    haptic("error", hapticsEnabled);
    sound("error", soundEnabled);
  }

  function playCard(cardId: string, pileIndex: 0 | 1): void {
    haptic("card", hapticsEnabled);
    sound("card", soundEnabled);
    onPlay(cardId, pileIndex);
  }

  function callChin(): void {
    if (chinSubmitLocked.current || chinDisabled) return;
    chinSubmitLocked.current = true;
    setLocalChinPending(true);
    setChinBurst(true);
    haptic("chin", hapticsEnabled);
    // El acierto lo confirma el servidor; el toque sólo acusa recepción.
    sound("ready", soundEnabled);
    onChin();
  }

  return (
    <main className={`game-shell ${hot ? "has-chin" : ""} ${room.stalled ? "is-stalled" : ""}`}>
      <header className="game-toolbar">
        <span className="room-mini-code">MESA <b>{room.code}</b></span>
        {seriesActive ? (
          <span className="series-chip" aria-label={`Serie: vos ${self.wins}, rival ${rival.wins}`}>
            {self.wins}–{rival.wins}
          </span>
        ) : null}
        <span className={`network-pill network-pill--${connection}`} aria-label={connection === "online" ? `Conectado, ${latency ?? 0} milisegundos` : "Sin conexión"}>
          {connection === "online" ? <Wifi aria-hidden="true" size={15} /> : <WifiOff aria-hidden="true" size={15} />}
          {connection === "online" ? `${latency ?? "—"} ms` : "Reconectando"}
        </span>
        <div className="toolbar-actions">
          <button className="icon-button icon-button--table" type="button" onClick={onToggleSound} aria-label={soundEnabled ? "Silenciar sonidos" : "Activar sonidos"}>
            {soundEnabled ? <Volume2 aria-hidden="true" size={20} /> : <VolumeX aria-hidden="true" size={20} />}
          </button>
          <button className="icon-button icon-button--table" type="button" onClick={onOpenRules} aria-label="Ver reglas">
            <BookOpen aria-hidden="true" size={20} />
          </button>
          <button className="icon-button icon-button--table" type="button" onClick={onLeave} aria-label="Abandonar partida">
            <LogOut aria-hidden="true" size={20} />
          </button>
        </div>
      </header>

      <section className={`player-zone player-zone--rival ${rival.handMode ? "is-hand-mode" : ""} ${cardsLeft(rival) <= LOW_CARDS_THRESHOLD ? "is-low-cards" : ""}`} aria-label="Zona del rival">
        <div className="player-zone__meta">
          <PlayerPanel player={rival} total={totalPerPlayer} />
          {!rival.handMode ? <Stock count={rival.stockCount} owner={rival.name} /> : null}
        </div>
        {rival.handMode ? (
          <HiddenHand player={rival} />
        ) : (
          <Rack
            player={rival}
            room={room}
            own={false}
            locked
            onPlay={onPlay}
            onInvalid={invalidCard}
          />
        )}
      </section>

      <section className="table-center" aria-label="Pilas centrales">
        <div className={`center-piles ${centerPulsing ? "is-refreshed" : ""}`}>
          <CenterPile card={room.piles[0].top} count={room.piles[0].count} index={0} hot={hot} />
          <CenterPile card={room.piles[1].top} count={room.piles[1].count} index={1} hot={hot} />
        </div>

        <button
          type="button"
          className={`chin-button ${hot && !chinDisabled ? "is-live" : ""} ${chinLocked ? "is-cooling" : ""} ${chinBusy ? "is-pending" : ""} ${protectionBlocking ? "is-protected" : ""} ${chinBurst ? "is-burst" : ""}`}
          onClick={callChin}
          aria-label={chinAnnouncement || (chinLocked ? "CHIN en pausa por cantar mal" : room.stalled ? "CHIN bloqueado mientras se destraba la mesa" : "Cantar CHIN; si te equivocás levantás el pozo")}
          aria-busy={chinBusy}
          disabled={chinDisabled}
        >
          <span>{chinKicker}</span>
          <strong>{chinMain}</strong>
        </button>
        <span className="sr-only" role="status" aria-live="assertive" aria-atomic="true">
          {chinAnnouncement}
        </span>

        {room.stalled && !paused ? (
          selfMustRefresh ? (
            <button className="stalled-button" type="button" onClick={onRefresh}>
              <strong>Te toca destrabar</strong>
              <span>Dar dos cartas nuevas</span>
            </button>
          ) : (
            <div className="stalled-status" role="status" aria-live="polite">
              <strong>Sin jugadas</strong>
              <span>Esperando a {stalledPlayer?.name ?? "tu rival"}</span>
            </div>
          )
        ) : null}
      </section>

      <section className={`player-zone player-zone--self ${self.handMode ? "is-hand-mode" : ""} ${cardsLeft(self) <= LOW_CARDS_THRESHOLD ? "is-low-cards" : ""}`} aria-label="Tu zona">
        {!self.handMode ? <div className="self-instruction"><span>Arrastrá una carta hasta el centro</span></div> : null}
        {self.handMode ? (
          <OwnHand
            player={self}
            room={room}
            locked={interactionLocked}
            onPlay={playCard}
            onInvalid={invalidCard}
          />
        ) : (
          <Rack
            player={self}
            room={room}
            own
            locked={interactionLocked}
            onPlay={playCard}
            onInvalid={invalidCard}
          />
        )}
        <div className="player-zone__meta player-zone__meta--self">
          <PlayerPanel player={self} total={totalPerPlayer} />
          {!self.handMode ? <Stock count={self.stockCount} owner="tu mazo" /> : null}
          <div className="emote-bar" aria-label="Mandar un gesto">
            {(Object.keys(EMOTE_EMOJI) as EmoteKind[]).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => onEmote(kind)}
                disabled={finished}
                aria-label={`Mandar ${EMOTE_EMOJI[kind]}`}
              >
                {EMOTE_EMOJI[kind]}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="emote-layer" aria-hidden="true">
        {emotes.map((event) => (
          <span
            key={event.id}
            className={`emote-bubble emote-bubble--${event.playerId === room.selfId ? "self" : "rival"}`}
          >
            {EMOTE_EMOJI[event.emote]}
          </span>
        ))}
      </div>

      {paused && !finished ? (
        <div className="state-overlay" role="status" aria-live="assertive">
          <span className="state-overlay__spinner" aria-hidden="true" />
          <strong>{connection === "online" ? "Esperando al rival" : "Recuperando conexión"}</strong>
          <p>La mesa está pausada y tu lugar sigue guardado.</p>
        </div>
      ) : null}

      {finished ? (
        <div className="state-overlay state-overlay--finished" role="dialog" aria-modal="true" aria-labelledby="result-title">
          <span className="result-kicker">PARTIDA TERMINADA</span>
          <strong id="result-title">{room.winnerId === null ? "Empate técnico" : won ? "¡Ganaste!" : `Ganó ${winner?.name ?? "tu rival"}`}</strong>
          <p>{won ? "Te quedaste sin cartas primero." : "La revancha está a un toque."}</p>
          {seriesActive ? (
            <span className="result-series" aria-label={`Serie: vos ${self.wins}, rival ${rival.wins}`}>
              Serie <b>{self.wins}–{rival.wins}</b>
            </span>
          ) : null}
          <div className="result-score">
            <span><b>{self.chinCount}</b> tus CHIN</span>
            <i aria-hidden="true" />
            <span><b>{rival.chinCount}</b> CHIN rival</span>
          </div>
          <button className="primary-button" type="button" onClick={onReady}>Jugar revancha</button>
          <button className="text-button" type="button" onClick={onLeave}>Volver al inicio</button>
        </div>
      ) : null}
    </main>
  );
}
